import { OFFICIAL_SOURCES } from '../officialSources';

export const EBC_GUIDED_META = {
	productName: 'Guided Journey',
	tagline: 'Always know the next step — complete each task with confidence.',

	preparationTimeline: [
		{ daysBefore: 90, label: 'Passport validity', task: 'Check passport is valid 6+ months beyond return date', stepId: 'visa' },
		{ daysBefore: 75, label: 'Book international flights', task: 'Book return flights to Kathmandu. Avoid tight Lukla connection dates.', stepId: 'flights' },
		{ daysBefore: 60, label: 'Buy travel insurance', task: 'Purchase policy with 5,500m altitude + helicopter evacuation cover', stepId: 'insurance' },
		{ daysBefore: 45, label: 'Start altitude training', task: 'Hike hills/stairs 3× weekly with 8kg pack. Break in boots.', stepId: 'packing' },
		{ daysBefore: 30, label: 'Buy core gear', task: 'Boots, sleeping bag, down jacket — or confirm Kathmandu rental plan', stepId: 'packing' },
		{ daysBefore: 14, label: 'Apply for visa', task: 'Complete online form at nepaliport.immigration.gov.np or plan VOA at airport', stepId: 'visa' },
		{ daysBefore: 7, label: 'Withdraw / exchange currency', task: 'Get USD for visa + EUR cash card for ATM in Kathmandu', stepId: 'kathmandu' },
		{ daysBefore: 3, label: 'Check weather & flights', task: 'Confirm Lukla booking, pack duffel under 15kg, download offline maps', stepId: 'lukla' },
		{ daysBefore: 0, label: 'Arrive Kathmandu', task: 'SIM, ATM, hotel check-in, rest — do not trek same day', stepId: 'kathmandu' },
		{ daysBefore: -1, label: 'Trek begins', task: 'Fly Lukla, get Khumbu permit, start walking', stepId: 'trek' },
	],

	budget: {
		currency: 'USD',
		tiers: [
			{ id: 'budget', label: 'Budget', range: '$1,200–1,500', description: 'Independent teahouse trek, no guide' },
			{ id: 'standard', label: 'Standard', range: '$1,800–2,200', description: 'Guide or porter, comfortable lodges' },
			{ id: 'comfort', label: 'Comfort', range: '$2,500–3,500', description: 'Full package, private room, helicopter backup' },
		],
		items: [
			{ id: 'flights-intl', label: 'International flights', budget: 700, standard: 900, comfort: 1200 },
			{ id: 'flights-lukla', label: 'Lukla round trip', budget: 180, standard: 280, comfort: 400 },
			{ id: 'visa', label: 'Visa (30 days)', budget: 50, standard: 50, comfort: 50 },
			{ id: 'insurance', label: 'Travel insurance', budget: 60, standard: 100, comfort: 150 },
			{ id: 'permits', label: 'Permits (both)', budget: 37, standard: 37, comfort: 37 },
			{ id: 'guide', label: 'Guide (12 days)', budget: 0, standard: 350, comfort: 420 },
			{ id: 'porter', label: 'Porter (12 days)', budget: 0, standard: 200, comfort: 250 },
			{ id: 'food-trail', label: 'Food on trail (12 days)', budget: 300, standard: 400, comfort: 500 },
			{ id: 'lodging-trail', label: 'Lodging on trail', budget: 120, standard: 200, comfort: 350 },
			{ id: 'kathmandu', label: 'Kathmandu (hotel, meals, gear)', budget: 150, standard: 250, comfort: 400 },
			{ id: 'tips', label: 'Tips (guide/porter/staff)', budget: 50, standard: 100, comfort: 150 },
			{ id: 'shopping', label: 'Shopping & souvenirs', budget: 50, standard: 100, comfort: 200 },
			{ id: 'emergency', label: 'Emergency fund (cash reserve)', budget: 100, standard: 150, comfort: 200 },
		],
	},

	documentsWallet: [
		{ id: 'passport', label: 'Passport', note: 'Valid 6+ months beyond return' },
		{ id: 'visa', label: 'Nepal visa', note: '30-day tourist visa' },
		{ id: 'insurance', label: 'Travel insurance policy', note: '5,500m altitude cover' },
		{ id: 'flight-intl', label: 'International flight ticket', note: 'Home city ↔ Kathmandu' },
		{ id: 'flight-lukla', label: 'Lukla flight ticket', note: 'KTM ↔ LUA round trip' },
		{ id: 'permit-sagarmatha', label: 'Sagarmatha National Park permit', note: 'Obtain in Kathmandu or Monjo' },
		{ id: 'permit-khumbu', label: 'Khumbu Pasang Lhamu permit', note: 'Obtain in Lukla on arrival' },
		{ id: 'hotel', label: 'Hotel booking confirmation', note: 'Thamel, first/last nights' },
		{ id: 'emergency', label: 'Emergency contacts card', note: 'Insurer, Tourist Police 1144, embassy' },
		{ id: 'photos', label: 'Passport photos (×4)', note: 'For permits and visa' },
	],

	packingChecklist: [
		{ id: 'boots', label: 'Hiking boots (broken in 4+ weeks)', category: 'Footwear', weight: '1.2 kg', buyAtHome: 'Outdoor retailer', rentKtm: 'From $2/day in Thamel', needOct: true },
		{ id: 'socks', label: 'Trekking socks (×4 pairs)', category: 'Footwear', weight: '200g', buyAtHome: 'Any outdoor shop', rentKtm: 'Buy locally', needOct: true },
		{ id: 'gaiters', label: 'Gaiters', category: 'Footwear', weight: '150g', buyAtHome: 'Optional for dry season', rentKtm: '$1/day', needOct: false },
		{ id: 'sleeping-bag', label: 'Sleeping bag (-15°C rated)', category: 'Sleep', weight: '1.5 kg', buyAtHome: 'Outdoor specialty shop', rentKtm: '$3/day', needOct: true },
		{ id: 'liner', label: 'Silk sleeping bag liner', category: 'Sleep', weight: '150g', buyAtHome: 'Outdoor / travel shop', rentKtm: 'Buy locally', needOct: true },
		{ id: 'down-jacket', label: 'Packable down jacket', category: 'Clothing', weight: '400g', buyAtHome: 'Outdoor brand store', rentKtm: '$2/day', needOct: true },
		{ id: 'rain-shell', label: 'Waterproof rain jacket', category: 'Clothing', weight: '300g', buyAtHome: 'Any outdoor brand', rentKtm: '$2/day', needOct: true },
		{ id: 'fleece', label: 'Fleece mid-layer', category: 'Clothing', weight: '350g', buyAtHome: 'Any', rentKtm: 'Buy in Thamel', needOct: true },
		{ id: 'base-layers', label: 'Base layers (×2)', category: 'Clothing', weight: '300g', buyAtHome: 'Merino recommended', rentKtm: 'Buy locally', needOct: true },
		{ id: 'trek-pants', label: 'Trekking pants (×2)', category: 'Clothing', weight: '500g', buyAtHome: 'Any', rentKtm: 'Buy locally', needOct: true },
		{ id: 'gloves', label: 'Insulated gloves + liner', category: 'Clothing', weight: '200g', buyAtHome: 'Outdoor shop', rentKtm: '$1/day', needOct: true },
		{ id: 'hat-warm', label: 'Warm beanie', category: 'Clothing', weight: '80g', buyAtHome: 'Any', rentKtm: 'Buy in Thamel', needOct: true },
		{ id: 'hat-sun', label: 'Sun hat / cap', category: 'Clothing', weight: '80g', buyAtHome: 'Any', rentKtm: 'Buy locally', needOct: true },
		{ id: 'sunglasses', label: 'Sunglasses (UV400)', category: 'Clothing', weight: '50g', buyAtHome: 'Required — bright at altitude', rentKtm: 'Buy locally', needOct: true },
		{ id: 'poles', label: 'Trekking poles (×2)', category: 'Gear', weight: '500g', buyAtHome: 'Outdoor shop', rentKtm: '$1/day', needOct: true },
		{ id: 'daypack', label: 'Daypack (25–35L)', category: 'Gear', weight: '800g', buyAtHome: 'Carry on trail daily', rentKtm: 'Rarely rented', needOct: true },
		{ id: 'duffel', label: 'Duffel bag (for porter, 60–70L)', category: 'Gear', weight: '1 kg', buyAtHome: 'Soft bag — no wheels', rentKtm: 'Often provided by agency', needOct: true },
		{ id: 'headlamp', label: 'Headlamp + spare batteries', category: 'Gear', weight: '100g', buyAtHome: 'Any', rentKtm: 'Buy in Thamel', needOct: true },
		{ id: 'water', label: 'Water bottles (2L total) + purification', category: 'Gear', weight: '200g', buyAtHome: 'Tablets or filter', rentKtm: 'Buy locally', needOct: true },
		{ id: 'powerbank', label: 'Power bank (20,000mAh)', category: 'Electronics', weight: '350g', buyAtHome: 'Electronics retailer', rentKtm: 'Buy locally', needOct: true },
		{ id: 'adapter', label: 'Nepal plug adapter (Type C/D)', category: 'Electronics', weight: '50g', buyAtHome: 'Universal adapter', rentKtm: 'Buy at airport', needOct: true },
		{ id: 'sunscreen', label: 'Sunscreen SPF 50+', category: 'Health', weight: '100g', buyAtHome: 'Pharmacy', rentKtm: 'Buy in Thamel', needOct: true },
		{ id: 'firstaid', label: 'First aid kit + blister plasters', category: 'Health', weight: '200g', buyAtHome: 'Pharmacy — blister plasters essential', rentKtm: 'Pharmacy in Namche', needOct: true },
		{ id: 'diamox', label: 'Diamox (consult doctor first)', category: 'Health', weight: '50g', buyAtHome: 'Prescription if required in your country', rentKtm: 'Pharmacy', needOct: false },
		{ id: 'snacks', label: 'Energy bars / trail snacks', category: 'Food', weight: '500g', buyAtHome: 'Supermarket', rentKtm: 'Buy in Namche', needOct: true },
	],

	stepGuided: {
		dream: {
			objective: 'Confirm Everest Base Camp is the right trek for you',
			why: 'Saves money and disappointment — EBC demands fitness, time, and altitude tolerance',
			estimatedTime: '15 minutes',
			taskChecklist: [
				{ id: 'decided', label: 'I have decided to trek EBC' },
				{ id: 'fitness-honest', label: 'I honestly assessed my fitness level' },
				{ id: 'dates-rough', label: 'I have rough travel dates in mind' },
			],
		},
		research: {
			objective: 'Lock in season, duration, and budget before booking anything',
			why: 'October is peak season worldwide — prices and crowds differ hugely from March',
			estimatedTime: '30 minutes',
			prerequisites: ['dream'],
			taskChecklist: [
				{ id: 'season-chosen', label: 'Best season for your dates (Oct–Nov or Mar–May)' },
				{ id: 'duration-set', label: 'Trek duration decided (12–14 days standard)' },
				{ id: 'budget-range', label: 'Budget range understood ($1,200–2,500)' },
			],
		},
		visa: {
			objective: 'Secure a valid Nepal tourist visa before or on arrival',
			why: 'No visa = no entry. EBC needs a 30-day visa minimum',
			estimatedTime: '20 min online · 30–60 min at airport',
			prerequisites: ['research'],
			documentsRequired: ['Passport (MRP)', 'Passport photo', 'USD cash or card for fee'],
			taskChecklist: [
				{ id: 'visa-applied', label: 'Online visa form submitted (or VOA plan confirmed)' },
				{ id: 'visa-fee-ready', label: 'USD 50 ready for 30-day visa' },
				{ id: 'visa-stamped', label: 'Visa stamp received in passport' },
			],
			actions: [
				{
					id: 'apply-online',
					title: 'Apply online (recommended before you fly)',
					type: 'online',
					description: 'Complete the form before your flight — saves 20–30 minutes at TIA immigration.',
					officialUrl: OFFICIAL_SOURCES.immigrationOnline.url,
					officialLabel: 'Nepal Online Visa Application',
					bring: ['Passport details', 'Digital passport photo', 'Hotel address in Kathmandu'],
					processingTime: '15–20 minutes',
					mistakes: ['Waiting until landing to start the form', 'Using a non-machine-readable passport'],
				},
				{
					id: 'voa-airport',
					title: 'Or: visa on arrival at Kathmandu airport',
					type: 'visit',
					address: 'Tribhuvan International Airport, Immigration Hall',
					mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Tribhuvan+International+Airport+Kathmandu',
					hours: '24/7 (aligned with international arrivals)',
					bring: ['Passport', 'USD 50 cash (30-day)', 'Passport photo', 'Completed arrival card'],
					waitTime: '30–90 min in peak season (Oct–Nov)',
					fee: 'USD 50 (30 days)',
					mistakes: ['No USD cash', 'Wrong passport type', 'Not filling arrival card on plane'],
					officialUrl: OFFICIAL_SOURCES.immigrationTia.url,
				},
			],
			officialPdfs: [
				{ label: 'Tourist visa information', url: OFFICIAL_SOURCES.immigration.url },
				{ label: 'TIA arrival FAQ', url: OFFICIAL_SOURCES.immigrationTia.url },
			],
		},
		flights: {
			objective: 'Book international and Lukla flights with buffer days',
			why: 'Lukla weather delays are routine — tight connections cause missed international flights',
			estimatedTime: '1–2 hours research + booking',
			prerequisites: ['visa'],
			documentsRequired: ['Passport details for booking'],
			taskChecklist: [
				{ id: 'intl-booked', label: 'International flights booked (home → KTM)' },
				{ id: 'lukla-booked', label: 'Lukla round trip booked or agency-confirmed' },
				{ id: 'buffer-days', label: '2–3 buffer days added in Kathmandu itinerary' },
			],
			actions: [
				{
					id: 'book-intl',
					title: 'Book international flights to Kathmandu',
					type: 'task',
					description: 'Book 75+ days ahead for October. Arrive at least 2 full days before Lukla flight.',
					mistakes: ['Scheduling Lukla same day as international arrival', 'No buffer for weather delays on return'],
				},
				{
					id: 'book-lukla',
					title: 'Book Kathmandu → Lukla domestic flights',
					type: 'task',
					description: 'Book through Tara Air, Summit Air, or your trekking agency. Confirm baggage limit (10–15 kg).',
					officialUrl: OFFICIAL_SOURCES.caan.url,
					mistakes: ['Booking before international flights confirmed', 'Packing over 15kg'],
				},
			],
		},
		insurance: {
			objective: 'Purchase travel insurance with 5,500m altitude and helicopter evacuation',
			why: 'Helicopter rescue costs $5,000–15,000+ without cover — non-negotiable for EBC',
			estimatedTime: '30–45 minutes',
			prerequisites: ['research'],
			documentsRequired: ['Policy document', 'Emergency phone number'],
			taskChecklist: [
				{ id: 'policy-purchased', label: 'Insurance policy purchased' },
				{ id: 'altitude-verified', label: 'Altitude limit verified (5,500m+)' },
				{ id: 'evacuation-verified', label: 'Helicopter evacuation confirmed in policy' },
				{ id: 'emergency-saved', label: 'Emergency number saved offline (phone + paper)' },
			],
			actions: [
				{
					id: 'buy-insurance',
					title: 'Buy and verify your trekking insurance',
					type: 'online',
					description: 'Read altitude exclusions. Standard policies cap at 3,000m — insufficient for Kala Patthar (5,545m).',
					bring: ['Trek dates', 'Maximum altitude (5,545m)', 'Activity type (high-altitude trekking)'],
					mistakes: ['Assuming "worldwide cover" includes 5,000m+', 'Not saving emergency number offline'],
				},
			],
		},
		packing: {
			objective: 'Pack every item needed for 12 days at altitude',
			why: 'No gear shops above Namche. Wrong boots or inadequate sleeping bag ends treks.',
			estimatedTime: '2–3 hours over several weeks',
			prerequisites: ['research'],
			usesPackingChecklist: true,
			taskChecklist: [
				{ id: 'boots-broken-in', label: 'Boots broken in (4+ weeks of wear)' },
				{ id: 'duffel-under-15kg', label: 'Duffel packed under 15kg for Lukla flight' },
				{ id: 'checklist-complete', label: 'Full packing checklist completed' },
			],
			actions: [
				{
					id: 'pack-october',
					title: 'Pack for October conditions',
					type: 'info',
					description: 'Namche nights: 0–5°C. Lobuche/Gorak Shep: -10 to -20°C. Layer system essential. Crampons NOT needed on standard EBC trail. Gaiters optional in dry season.',
					mistakes: ['Underpacking warm layers', 'Overpacking — exceeding Lukla weight limit', 'Buying boots in Kathmandu day before trek'],
				},
			],
		},
		permits: {
			objective: 'Understand which permits you need and total cost',
			why: 'Trekking without permits = fine + forced turnaround at Monjo checkpoint',
			estimatedTime: '10 minutes to read · 1 hour to obtain',
			prerequisites: ['flights'],
			documentsRequired: ['Passport', 'Passport photos', 'NPR cash'],
			taskChecklist: [
				{ id: 'permits-understood', label: 'I know I need 2 permits (not TIMS)' },
				{ id: 'cash-prepared', label: 'NPR 6,000 cash prepared for both permits' },
			],
			actions: [
				{
					id: 'permit-summary',
					title: 'Your two permits',
					type: 'info',
					description: '1) Sagarmatha National Park — NPR 3,000 (+VAT). 2) Khumbu Pasang Lhamu — NPR 2,000. Total ~NPR 5,000–6,000. TIMS not required.',
					fee: 'NPR ~5,000–6,000 total',
					officialUrl: OFFICIAL_SOURCES.dnp.url,
					mistakes: ['Thinking TIMS is required', 'Bringing only USD — permits are NPR only'],
				},
			],
			officialPdfs: [
				{ label: 'DNPWC — National Parks', url: OFFICIAL_SOURCES.dnp.url },
				{ label: 'NTB — Trekking permits', url: OFFICIAL_SOURCES.ntb.url },
			],
		},
		'permit-offices': {
			objective: 'Obtain both permits before Monjo checkpoint',
			why: 'Without permits you cannot enter Sagarmatha National Park — the trek ends at Monjo',
			estimatedTime: '45 min (KTM) + 20 min (Lukla)',
			prerequisites: ['permits', 'kathmandu'],
			documentsRequired: ['Passport (original)', 'Passport photocopy', '2 passport-size photos', 'NPR cash'],
			taskChecklist: [
				{ id: 'sagarmatha-obtained', label: 'Sagarmatha National Park permit obtained' },
				{ id: 'khumbu-obtained', label: 'Khumbu Pasang Lhamu permit obtained in Lukla' },
			],
			actions: [
				{
					id: 'visit-ntb',
					title: 'Step 1: Go to Nepal Tourism Board Office (Kathmandu)',
					type: 'visit',
					description: 'Get your Sagarmatha National Park entry permit here before flying to Lukla.',
					address: 'Exhibition Road, Bhrikutimandap, Kathmandu 44600',
					mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Nepal+Tourism+Board+Bhrikutimandap+Kathmandu',
					hours: 'Sun–Fri 09:00–17:00 (closed Saturdays & Nepal public holidays)',
					bring: ['✓ Passport (original)', '✓ Passport photocopy', '✓ 2 passport-size photos', '✓ NPR 3,400 cash (NPR 3,000 + VAT buffer)'],
					waitTime: '15–45 min (Oct–Nov peak: up to 90 min)',
					fee: 'NPR 3,000 + 13% VAT',
					processingTime: 'Same day, issued on the spot',
					mistakes: ['Arriving on Saturday (closed)', 'No NPR cash — USD not accepted', 'Forgetting passport photos', 'Going after 15:00 and finding counter closed'],
					officialUrl: 'https://ntb.gov.np/en/plan-your-trip/before-you-come/park-entry-fees',
					officialLabel: 'NTB park entry fees (official)',
				},
				{
					id: 'visit-lukla',
					title: 'Step 2: Get Khumbu permit in Lukla (on arrival)',
					type: 'visit',
					description: 'Immediately after landing at Tenzing-Hillary Airport — before you start walking.',
					address: 'Permit counter near Lukla Airport exit, Khumbu Pasang Lhamu RM',
					mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Lukla+Airport+Nepal',
					hours: 'Aligned with morning flight arrivals (~06:00–12:00)',
					bring: ['✓ Passport (original)', '✓ NPR 2,000 cash per person'],
					waitTime: '10–30 min (Oct peak: 45+ min)',
					fee: 'NPR 2,000',
					processingTime: 'Immediate',
					mistakes: ['Trying to buy this permit in Kathmandu (not possible)', 'Starting trek without it', 'No exact change'],
				},
			],
			alternatives: [
				'Hire a Thamel trekking agency to arrange both permits (service fee NPR 1,000–2,000)',
				'Buy Sagarmatha permit at Monjo gate if you missed Kathmandu (adds delay on trek day 2)',
			],
		},
		guide: {
			objective: 'Decide: independent trek or hire a licensed guide',
			why: 'Guides help with navigation, altitude judgment, and lodge booking in peak season',
			estimatedTime: '30 minutes to decide · 1 hour to book',
			taskChecklist: [
				{ id: 'guide-decision', label: 'Decision made: solo or guided' },
				{ id: 'guide-booked', label: 'Guide booked (if hiring)' },
			],
			actions: [
				{
					id: 'guide-decide',
					title: 'Make your guide decision',
					type: 'task',
					description: 'EBC classic route: guide not mandatory. First-time high-altitude trekkers strongly benefit from a guide. Independent trekking is common and well-marked.',
					mistakes: ['Hiring unlicensed guides at the airport', 'Not verifying NTB policy before booking'],
					officialUrl: OFFICIAL_SOURCES.ntb.url,
				},
			],
		},
		sherpa: {
			objective: 'If hiring, book a licensed guide through a registered agency',
			why: 'Licensed guides know altitude protocols, trail conditions, and emergency procedures',
			estimatedTime: '1 hour research + booking',
			prerequisites: ['guide'],
			taskChecklist: [
				{ id: 'agency-chosen', label: 'Registered agency chosen (TAAN member preferred)' },
				{ id: 'guide-confirmed', label: 'Guide confirmed with written itinerary' },
				{ id: 'deposit-paid', label: 'Deposit paid (not 100% upfront)' },
			],
			actions: [
				{
					id: 'hire-agency',
					title: 'Book through a registered trekking agency in Thamel',
					type: 'visit',
					address: 'Thamel, Kathmandu — visit in person before paying',
					mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Thamel+Kathmandu+trekking+agency',
					description: 'USD 25–35/day for guide. Full package $1,400–2,800 for 12–14 days including flights, permits, lodges.',
					mistakes: ['Paying 100% upfront in cash', 'No written itinerary', 'Agency with no physical office', 'Prices far below market'],
					officialUrl: OFFICIAL_SOURCES.tourismDept.url,
				},
			],
		},
		porter: {
			objective: 'Decide if you need a porter and understand weight limits',
			why: 'Walking with 5kg daypack vs 15kg duffel changes your trek experience dramatically',
			estimatedTime: '15 minutes',
			taskChecklist: [
				{ id: 'porter-decision', label: 'Porter decision made' },
				{ id: 'porter-booked', label: 'Porter booked (if hiring)' },
				{ id: 'duffel-packed', label: 'Duffel packed ≤15kg for porter' },
			],
			actions: [
				{
					id: 'porter-info',
					title: 'Porter logistics',
					type: 'info',
					description: 'USD 15–22/day. Carries max 15kg duffel. You walk with 5–8kg daypack. Tip 10–15% at trek end.',
					mistakes: ['Overloading porter beyond 15kg', 'Giving porter your daypack instead of duffel'],
				},
			],
		},
		kathmandu: {
			objective: 'Complete Kathmandu prep: hotel, SIM, cash, gear, Sagarmatha permit',
			why: 'Everything above Lukla is cash-only with limited shops — Kathmandu is your last full-service stop',
			estimatedTime: '1–2 days',
			prerequisites: ['flights', 'visa'],
			documentsRequired: ['Passport', 'Cash card', 'Hotel confirmation'],
			taskChecklist: [
				{ id: 'hotel-booked', label: 'Hotel booked in Thamel (first + last nights)' },
				{ id: 'sim-purchased', label: 'Ncell or NTC SIM purchased' },
				{ id: 'cash-withdrawn', label: 'NPR 50,000–80,000 withdrawn from ATM' },
				{ id: 'gear-ready', label: 'Gear purchased or rented' },
			],
			actions: [
				{
					id: 'buy-sim',
					title: 'Buy a SIM card (do this on arrival day)',
					type: 'visit',
					address: 'Ncell or NTC store, Thamel — or airport counter',
					mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Ncell+Thamel+Kathmandu',
					bring: ['✓ Passport copy', '✓ Passport photo', '✓ NPR 500–1,500 for data package'],
					description: 'Ncell has better Khumbu coverage. Essential for Lukla delay coordination.',
					mistakes: ['Waiting until trek to get SIM', 'Buying from unofficial street vendors'],
				},
				{
					id: 'withdraw-cash',
					title: 'Withdraw trek cash from ATM',
					type: 'visit',
					address: 'Thamel ATMs: Nabil Bank, Standard Chartered, Himalayan Bank',
					mapsUrl: 'https://www.google.com/maps/search/?api=1&query=ATM+Thamel+Kathmandu',
					bring: ['✓ International debit card (notify bank before travel)'],
					description: 'Withdraw NPR 50,000–80,000. No reliable ATMs above Namche. Trail is cash-only.',
					mistakes: ['Assuming cards work on trail', 'Not notifying home bank before travel'],
				},
				{
					id: 'rent-gear',
					title: 'Rent or buy last-minute gear in Thamel',
					type: 'visit',
					address: 'Thamel gear shops — Shona\'s Alpine area',
					mapsUrl: 'https://www.google.com/maps/search/?api=1&query=trekking+gear+rental+Thamel',
					description: 'Sleeping bag $3/day, down jacket $2/day, poles $1/day. Do NOT buy boots here unless you have weeks to break them in.',
					mistakes: ['Buying boots day before Lukla flight', 'Not inspecting rented gear zippers'],
				},
			],
		},
		lukla: {
			objective: 'Fly to Lukla, get Khumbu permit, and start trekking',
			why: 'This is day 1 of your trek — weather determines everything',
			estimatedTime: 'Full day (flight + 3–4 hr walk)',
			prerequisites: ['permit-offices', 'packing', 'kathmandu'],
			taskChecklist: [
				{ id: 'lukla-flown', label: 'Lukla flight completed' },
				{ id: 'khumbu-permit', label: 'Khumbu permit obtained in Lukla' },
				{ id: 'trek-started', label: 'Trek started — walked to Phakding' },
			],
			actions: [
				{
					id: 'lukla-flight-day',
					title: 'Lukla flight day checklist',
					type: 'task',
					description: 'Wear boots and jacket on body. Check in 1 hour early. Baggage max 10–15kg. Get Khumbu permit at airport exit before walking.',
					bring: ['✓ Lukla flight ticket', '✓ Passport', '✓ NPR 2,000 for Khumbu permit', '✓ Daypack with water and snacks'],
					mistakes: ['Packing boots in checked bag', 'Starting trek without Khumbu permit', 'Walking too fast on day 1'],
					officialUrl: OFFICIAL_SOURCES.caan.url,
				},
			],
		},
		trek: {
			objective: 'Complete the 12-day trek to Everest Base Camp and Kala Patthar',
			why: 'This is why you came',
			estimatedTime: '12–14 days',
			prerequisites: ['lukla'],
			taskChecklist: [
				{ id: 'reached-ebc', label: 'Reached Everest Base Camp (5,364m)' },
				{ id: 'kala-patthar', label: 'Summited Kala Patthar sunrise (5,545m)' },
				{ id: 'returned-lukla', label: 'Returned to Lukla on foot' },
			],
		},
		emergency: {
			objective: 'Know emergency procedures before you need them',
			why: 'At 5,000m+, help is hours away — preparation saves lives',
			estimatedTime: '20 minutes to read and save contacts',
			taskChecklist: [
				{ id: 'ams-learned', label: 'AMS symptoms and response learned' },
				{ id: 'contacts-saved', label: 'Tourist Police 1144 + insurer number saved offline' },
				{ id: 'insurance-card', label: 'Insurance card photo saved on phone' },
			],
			actions: [
				{
					id: 'emergency-contacts',
					title: 'Save these emergency contacts offline',
					type: 'info',
					description: 'Tourist Police: 1144 (toll-free, English, 24/7). Insurer emergency line. CIWEC Clinic: +977-1-4424111. HRA Namche post.',
					bring: ['✓ Write on paper', '✓ Save in phone', '✓ Share with trekking partner'],
					mistakes: ['Only saving contacts online — no signal at altitude', 'Ignoring mild AMS symptoms'],
				},
			],
		},
		return: {
			objective: 'Fly back, shop, and depart Nepal safely',
			why: 'Close the loop — buffer days prevent missed international flights',
			estimatedTime: '2–3 days',
			taskChecklist: [
				{ id: 'lukla-return-flight', label: 'Lukla → Kathmandu return flight completed' },
				{ id: 'souvenirs', label: 'Souvenirs purchased (with export certificates if needed)' },
				{ id: 'intl-departure', label: 'International departure completed' },
			],
		},
	},
};
